import { DEFAULT_KDS_THEME_ID, DEFAULT_THEME_ID, ThemeProvider } from '@qrkey/ui';
import type { ReactNode } from 'react';
import { useBootstrap, useCurrentOutlet, useMe } from '../screens/Shell.js';
import { useSession } from './api.js';
import { useTerminal } from './terminal.js';

/**
 * The theme a person gets before they pick one (docs/design/theming.md "Who chooses
 * what"): a KDS terminal starts dark; any other screen starts from the outlet's own
 * `ui.theme`; the product default until the outlet's settings have loaded.
 */
export function defaultStaffTheme(
  terminalRole: string | undefined,
  outletTheme: string | undefined,
): string {
  if (terminalRole === 'kds') return DEFAULT_KDS_THEME_ID;
  return outletTheme ?? DEFAULT_THEME_ID;
}

/**
 * Each person's theme on this device. Their own pick (Settings > Appearance, kept per
 * user) always wins; until they make one, the outlet's `ui.theme` from the bootstrap
 * bundle is applied, and follows the outlet's setting if it changes.
 */
export function StaffThemeProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const terminal = useTerminal();
  const me = useMe();
  const [outletId] = useCurrentOutlet(me.data);
  const bootstrap = useBootstrap(outletId);
  const outletTheme = bootstrap.data?.settings['ui.theme'];
  return (
    <ThemeProvider
      app="staff"
      scope={session?.session.userId ?? null}
      defaultThemeId={defaultStaffTheme(terminal?.role, outletTheme)}
    >
      {children}
    </ThemeProvider>
  );
}
